import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useP } from '../i18n/useP';
import { exportTenant, importTenant } from '../api/dataExchange';
import './ReportsDataPage.css';

/* Icônes en ligne (export / import). */
const Icon = {
  upload: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17V5" /><polyline points="6 11 12 5 18 11" /><path d="M5 21h14" />
    </svg>
  ),
  download: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v12" /><polyline points="6 13 12 19 18 13" /><path d="M5 21h14" />
    </svg>
  ),
};

/**
 * Export / import Excel du tenant (échange de données). Embeddable : pas de
 * PageShell propre (le titre est porté par le hub).
 * `exportOnly` : Paramètres › Données, où le client exporte sans importer
 * (l'import reste à l'Administration, 2026-10-11).
 */
export default function DataExchangePage({ activeTenant, addToast, exportOnly = false }) {
  const { t } = useTranslation();
  const p = useP();
  const [exportingTenant, setExportingTenant] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importStrategy] = useState('ignore');  // upsert-only (param conservé pour la route)
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  async function handleExportTenant() {
    if (!activeTenant || exportingTenant) return;
    setExportingTenant(true);
    try { await exportTenant(activeTenant.id); }
    catch (err) { if (addToast) addToast(`${p("Erreur d'export")} : ${err.message}`, 'error'); }
    finally { setExportingTenant(false); }
  }

  function handleImportOpen() {
    setImportFile(null);
    setImportResult(null);
    setShowImport(true);
  }
  async function handleImportSubmit() {
    if (!importFile || importing) return;
    setImporting(true);
    setImportResult(null);
    try {
      const stats = await importTenant(activeTenant.id, importFile, importStrategy);
      setImportResult({ ok: true, stats });
    } catch (err) {
      setImportResult({ ok: false, message: err.message });
    } finally {
      setImporting(false);
    }
  }
  function handleImportClose() {
    setShowImport(false);
    setImportFile(null);
    setImportResult(null);
  }

  return (
    <div className="rdp__cols">
      <section className="rdp__section">
        <div className="lk-card">
          <div className="lk-card__head rdp__data-head">
            <span>{p('Échange de données')}</span>
            {activeTenant?.name && (
              <span className="rdp__data-tenant">{activeTenant.name}</span>
            )}
          </div>
          <div className="lk-card__body rdp__data-card">
            <div className="rdp__data-row">
              <div className="rdp__data-info">
                <div className="rdp__data-name">{p("Exporter les données de l'entreprise")}</div>
                <div className="rdp__data-desc">
                  {p("Toutes les données de l'entreprise active vers un fichier Excel (.xlsx).")}
                </div>
              </div>
              <button
                className="rdp__btn rdp__btn--primary"
                onClick={handleExportTenant}
                disabled={!activeTenant || exportingTenant}
              >
                {exportingTenant ? '…' : <>{Icon.download} {p('Export Excel')}</>}
              </button>
            </div>
            {!exportOnly && (
              <div className="rdp__data-row">
                <div className="rdp__data-info">
                  <div className="rdp__data-name">{p('Importer des données')}</div>
                  <div className="rdp__data-desc">
                    {p('Charger un fichier Excel exporté depuis Palestre.')}
                  </div>
                </div>
                <button
                  className="rdp__btn rdp__btn--import"
                  onClick={handleImportOpen}
                  disabled={!activeTenant}
                >
                  {Icon.upload} {p('Import Excel')}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── Modale d'import ─────────────────────────────────────────────── */}
      {showImport && !exportOnly && (
        <div className="topnav__modal-overlay">
          <div className="topnav__modal" onClick={e => e.stopPropagation()}>
            <div className="topnav__modal-header">
              <span className="topnav__modal-title">{p('Importer des données')}</span>
              <button className="topnav__modal-close" onClick={handleImportClose}>✕</button>
            </div>
            <div className="topnav__modal-body">
              <p className="topnav__modal-hint">
                {p("Charger un fichier Excel exporté depuis Palestre. L'export couvre toutes les tables de saisie du tenant, un onglet par table. Seuls les onglets présents sont traités. Mise à jour par ligne (id) : les lignes existantes sont mises à jour, les nouvelles ajoutées, rien n'est supprimé et une cellule vide ne remplace jamais une valeur existante.")}
              </p>
              <div className="topnav__modal-field">
                <label className="topnav__modal-label">{p('Fichier')}</label>
                <div className="topnav__file-row">
                  <span className="topnav__file-name">
                    {importFile ? importFile.name : p('Aucun fichier sélectionné')}
                  </span>
                  <button className="topnav__btn-browse" onClick={() => fileInputRef.current?.click()}>
                    {p('Parcourir…')}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={e => { setImportFile(e.target.files[0] || null); setImportResult(null); }}
                  />
                </div>
              </div>
              {importResult && (
                <div className={`topnav__import-result${importResult.ok ? '' : ' is-error'}`}>
                  {importResult.ok ? (
                    <>
                      <div className="topnav__import-stats">
                        <span>✓ {p('Créées :')} <strong>{importResult.stats.created}</strong></span>
                        <span>↻ {p('Mises à jour :')} <strong>{importResult.stats.overwritten}</strong></span>
                        <span>— {p('Ignorées :')} <strong>{importResult.stats.skipped}</strong></span>
                      </div>
                      {importResult.stats.errors?.length > 0 && (
                        <div className="topnav__import-errors">
                          {importResult.stats.errors.map((e, i) => (
                            <div key={i} className="topnav__import-error-line">⚠ {e}</div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="topnav__import-error-line">✕ {importResult.message}</div>
                  )}
                </div>
              )}
            </div>
            <div className="topnav__modal-footer">
              <button className="topnav__btn-cancel" onClick={handleImportClose}>
                {importResult?.ok ? t('common:actions.close') : t('common:actions.cancel')}
              </button>
              {!importResult?.ok && (
                <button
                  className="topnav__btn-import-confirm"
                  disabled={!importFile || importing}
                  onClick={handleImportSubmit}
                >
                  {importing ? p('Import…') : t('common:actions.import')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
